import { Mui, usePrevious } from "@osu/react-ui";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useFormSubmission from "../hooks/useFormSubmission";
import { links } from "../paths";
import StandardTemplate from "../templates/Standard";
import { ACTION_STATUS } from "../../util/constants";
import StudentInfo from "../../Form/containers/StudentInfo";

function Mapper(params) {
  const {
    creator,
    delegateId,
    editScreen,
    form,
    formSections,
    initialState,
    onSubmit,
    onSave,
    errorState,
    getSections,
    formType,
    formTypeFriendly,
    formId,
    isStudentForm,
    loadGetFormCall,
    loading,
    studentid,
    submissionId,
    submissionOSUID,
    submissionStatus,
    submissionErrorMessage,
    navigate,
    navigationMessage,
    location,
    match,
    resetDropdowns,
    resetFormError,
    resetSubmission,
    user,
    showSubmit,
    status,
    formPcdIndicator
  } = params;
  const osuid = match?.params?.osuid;
  const pathname = (location?.pathname ?? "");
  const { isGradPlanAdmin = false, isStudent = false } = user;
  const { canUserAccessFormType, canUserActOnForm } = useFormSubmission();
  const previousPathname = usePrevious(pathname);
  const alertRef = useRef(null);
  const [alert, setAlert] = useState(null);
  const previousAlert = usePrevious(alert);
  const [createAccessVerified, setCreateAccessVerified] = useState(false);
  const [editAccessVerified, setEditAccessVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(null);
  const [studentInfoHeading, setStudentInfoHeading] = useState("");
  const [studentInfoErrorMessage, setStudentInfoErrorMessage] = useState("");
  const [studentInfoId, setStudentInfoId] = useState(null);
  const previousStudentInfoId = usePrevious(studentInfoId);

  const canAccessFormType = canUserAccessFormType(formType);
  if(canAccessFormType === false) navigate("/unauthorized");

  const onAlertClose = (
    (alert && alert?.close === true) ? 
      () => {
        setAlert(null);
        if(alert.onClose) alert.onClose();
      } :
      null
  );

  const handleSave = (formData, stepState) => {
    if(errorState) resetFormError()
    setIsSubmitting(false);
    onSave({ id: formId, formType, formData, osuid, stepState, user, delegateId, creator });
    setAlert({ message: "Saving the form..." });
  };

  const handleSubmit = (formData, validity, stepState) => {
    if(errorState) resetFormError()
    setIsSubmitting(true);
    onSubmit({ id: formId, formType, formData, osuid, stepState, user });
    setAlert({ message: "Submitting the form..." });
  };

  // when there is an existing form, get the form data
  useEffect(() => {
    if(loadGetFormCall && formId) getSections({ id: formId, osuid, formType });
  }, [getSections, formId, formType, loadGetFormCall, osuid]);

  // when creating a form, verify that the user can create the form
  useEffect(() => {
    if(editScreen === false && createAccessVerified === false) {
      if(isStudentForm === true) {
        if(isStudent === false) navigate("/unauthorized");
      } else {
        if(isGradPlanAdmin === false) navigate("/unauthorized");
      }
      setCreateAccessVerified(true);
    }
  }, [createAccessVerified, editScreen, isGradPlanAdmin, isStudent, isStudentForm, navigate]);

  // when the existing form is available, verify that the user can act on the form
  useEffect(() => {
    if(editScreen === true && editAccessVerified === false && form) {
      if(canUserActOnForm(form) === false) navigate("/unauthorized");
      setEditAccessVerified(true);
    }
  }, [canUserActOnForm, editAccessVerified, editScreen, form, navigate]);

  // when there is a navigation message, navigate to the landing page to show the error
  useEffect(() => {
    if(navigationMessage) navigate(`/form/${formTypeFriendly}`, { state: { errorMessage: navigationMessage } });
  }, [navigationMessage, formTypeFriendly, navigate]);

  // when the alert changes, set focus
  useEffect(() => {
    if(!previousAlert && alert) { // alert is shown
      if(alertRef?.current) alertRef.current.focus(); // set focus on alert
    }
    if(previousAlert && !alert) { // alert is removed
      // set focus on header (so that focus is not lost)
      const h1 = document.getElementsByTagName("h1")?.[0];
      if(h1) h1.focus();
    }
  }, [alert, previousAlert]);

  // when the student info id changes, clear the error message
  useEffect(() => {
    if(previousStudentInfoId !== studentInfoId) setStudentInfoErrorMessage(null);
  }, [previousStudentInfoId, studentInfoId])

  // when the status changes, show an alert on error or navigate on success
  useEffect(() => {
    if(errorState) {
      setAlert({
        message: "Please correct the errors on the form before submitting.",
        severity: "error",
        close: true,
        onClose: () => (resetFormError())
      });
    } else {
      if(submissionStatus === ACTION_STATUS.ERROR) {
        const message = (submissionErrorMessage ?? `An error occurred while ${(isSubmitting ? "submitting" : "saving")} the form.`);
        setAlert({ message, severity: "error", close: true });
      }
      if(submissionStatus === ACTION_STATUS.SUCCESS) {
        const newPath = (isSubmitting ? "status" : "view");
        let newLocation;
        if(pathname.endsWith("create")) {
          newLocation = pathname.replace("create", newPath);
          newLocation += `/${submissionId}`;
          if(submissionOSUID) newLocation += `/${submissionOSUID}`;
        }
        if(pathname?.includes("/edit/")) newLocation = pathname.replace("/edit/", `/${newPath}/`);
        if(newLocation !== pathname) {
          let message = null;
          if(isSubmitting) { // form submit
            message = "Your form has been submitted.";
          } else { // form save
            if(delegateId) { // admin
              message = "This form has been successfully updated.";
            } else { // student
              message = "Your application has been saved but not submitted. Please review the application and submit when you are ready.";
            }
          }
          const options = { replace: true };
          const state = {};
          if(message) state.message = message;
          if(Object.keys(state).length > 0) options.state = state;
          navigate(newLocation, options);
        } else {
          resetSubmission(); // avoid additional triggers of this effect
          setAlert(null);
        }
      }
    }
    
  }, [delegateId, errorState, isSubmitting, location, navigate, pathname, resetFormError, resetSubmission, submissionErrorMessage, submissionId, submissionOSUID, submissionStatus]);

  // when navigation succeeds, reset the submission to avoid additional effects from triggering
  useEffect(() => {
    if(previousPathname && pathname !== previousPathname) resetSubmission();
  }, [pathname, previousPathname, resetSubmission])

  // on unmount, reset fields and data
  useEffect(() => {
    return () => {
      resetDropdowns();
      resetFormError();
      resetSubmission();
    };
    // eslint-disable-next-line
  }, []); // array is purposefully empty to force clean-up only on unmount

  return (formType ?
    (
      <Fragment>
        {alert?.message && 
          (<Mui.Alert ref={alertRef} tabIndex="-1" className="margin-bottom-2" severity={alert?.severity ?? "info"}
            sx={{ whiteSpace: "pre-line" }} onClose={onAlertClose}>
              {alert?.title && (<Mui.AlertTitle>{alert.title}</Mui.AlertTitle>)}
              {alert.message}
          </Mui.Alert>)
        }
        {(isStudentForm === true || !!studentInfoId) && (
          <Mui.Box marginBottom="1rem" style={{...(loading ? { display: "none" } : {})}}>
            {!!studentInfoHeading && (
              <Mui.Typography className="margin-y-2" variant="h5" component="h2">
                {studentInfoHeading}
              </Mui.Typography>
            )}
            {!!studentInfoErrorMessage && (
              <Mui.Alert className="margin-bottom-2" variant="outlined" severity="error">
                {studentInfoErrorMessage}
              </Mui.Alert>
            )}
            <StudentInfo osuid={(studentInfoId ?? osuid)} formId={formId} setHeading={setStudentInfoHeading} setErrorMessage={setStudentInfoErrorMessage} />
          </Mui.Box>
        )}
        <StandardTemplate
          formSections={formSections}
          showSubmit={showSubmit}
          onSubmit={handleSubmit}
          onSave={handleSave}
          initialState={initialState}
          errorState={errorState}
          loading={loading}
          setStudentInfoId={setStudentInfoId}
          delegateId={delegateId}
          studentid={studentid}
          formPcdIndicator={formPcdIndicator}
        />
      </Fragment>
    ) :
    (
      <Mui.List>
        {links.map((link) => (
          <Mui.ListItem>
            <Mui.Link component={Link} to={link.path}>
              {link.title}
            </Mui.Link>
          </Mui.ListItem>
        ))}
      </Mui.List>
    )
  );
}

export default Mapper;